import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api.js";
import { URLS, ImgURL } from "../utils/urls.js";
import { Save, ArrowLeft, Calendar, MapPin, Tag, Type, DollarSign, Image as ImageIcon, X } from "lucide-react";
import { toast } from "react-hot-toast";
import AdminMediaGallery from "./AdminMediaGallery.jsx";

const VIBE_OPTIONS = ['Wellness', 'Social', 'Growth', 'Creative', 'Healing', 'Brunch', 'Networking', 'Meditation', 'Deep Talk', 'Spa', 'Soft Life', 'Adventure', 'Outdoor', 'Festival', 'Nature'];

const EventEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setActionSaving] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    coverImageUrl: "",
    eventType: "In-Person",
    vibeTags: [],
    price: 0,
    eventStartAt: "",
    locationName: "",
    lat: "",
    long: "",
    status: "Published",
    isPublic: 1,
    featured: 0,
    externalTicketUrl: "",
    meetingLink: "",
    source: "Tribe25",
    gallery: []
  });

  const [activeMediaTarget, setActiveMediaTarget] = useState("cover"); // "cover" or "gallery"

  useEffect(() => {
    if (isEdit) {
      fetchEvent();
    }
  }, [id]);

  const fetchEvent = async () => {
    try {
      const res = await api.get(URLS.GET_SINGLE_EVENT(id));
      const event = res.data.event;
      
      let formattedDate = "";
      if (event.eventStartAt) {
        const d = new Date(event.eventStartAt);
        formattedDate = d.toISOString().slice(0, 16);
      }

      setFormData({
        title: event.title || "",
        description: event.description || "",
        coverImageUrl: event.coverImageUrl || "",
        eventType: event.eventType || "In-Person",
        vibeTags: event.vibeTags || [],
        price: event.price || 0,
        eventStartAt: formattedDate,
        locationName: event.locationName || "",
        lat: event.lat || "",
        long: event.long || "",
        status: event.status || "Published",
        isPublic: event.isPublic ?? 1,
        featured: event.featured || 0,
        externalTicketUrl: event.externalTicketUrl || "",
        meetingLink: event.meetingLink || "",
        source: event.source || "Tribe25",
        gallery: event.gallery || []
      });
    } catch (err) {
      toast.error("Failed to load event data");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVibeToggle = (vibe) => {
    setFormData(prev => {
        const current = [...prev.vibeTags];
        if (current.includes(vibe)) {
            return { ...prev, vibeTags: current.filter(v => v !== vibe) };
        } else {
            return { ...prev, vibeTags: [...current, vibe] };
        }
    });
  };

  const handleRemoveGalleryItem = (index) => {
    setFormData(prev => ({
        ...prev,
        gallery: prev.gallery.filter((_, i) => i !== index)
    }));
  };

  const handleMediaSelect = (media) => {
    if (activeMediaTarget === "cover") {
        setFormData(prev => ({ ...prev, coverImageUrl: media.url }));
    } else {
        setFormData(prev => ({ 
            ...prev, 
            gallery: [...prev.gallery, { url: media.url, mediaType: 'image' }] 
        }));
    }
    setShowGallery(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionSaving(true);

    try {
      const dataToSubmit = { ...formData };
      
      // Format date for MySQL
      if (dataToSubmit.eventStartAt) {
        dataToSubmit.eventStartAt = dataToSubmit.eventStartAt.replace('T', ' ') + ':00';
      }

      // Convert numeric fields from strings to numbers or null
      const numericFields = ['price', 'lat', 'long', 'capacity', 'totalSold'];
      numericFields.forEach(field => {
          if (dataToSubmit[field] === "" || dataToSubmit[field] === undefined) {
              dataToSubmit[field] = null;
          } else {
              dataToSubmit[field] = parseFloat(dataToSubmit[field]);
          }
      });

      if (isEdit) {
        await api.put(URLS.UPDATE_EVENT(id), dataToSubmit);
        toast.success("Event updated successfully");
      } else {
        await api.post(URLS.CREATE_EVENT, dataToSubmit);
        toast.success("Event created successfully");
      }
      navigate("/admin/events");
    } catch (err) {
      console.error("Save Error:", err);
      const msg = err.response?.data?.message || "Failed to save event";
      toast.error(msg);
    } finally {
      setActionSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-center text-gray-500 font-medium">Loading event data...</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/admin/events")}
          className="p-2 hover:bg-white rounded-xl transition shadow-sm text-gray-600"
        >
          <ArrowLeft size={20} />
        </button>
        <h2 className="text-2xl font-bold text-gray-800">
          {isEdit ? "Edit Event" : "Create New Event"}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl shadow-md p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                <Type size={14} className="text-[#db8654]" /> Event Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="e.g. Wellness Weekend Retreat"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#db8654] focus:ring-2 focus:ring-[#db8654]/20 outline-none transition"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
              <textarea
                name="description"
                rows="6"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="Detailed information about the event..."
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#db8654] focus:ring-2 focus:ring-[#db8654]/20 outline-none transition resize-none"
              ></textarea>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                        <Calendar size={14} className="text-[#db8654]" /> Start Date & Time
                    </label>
                    <input
                        type="datetime-local"
                        name="eventStartAt"
                        value={formData.eventStartAt}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#db8654] outline-none transition"
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                        <MapPin size={14} className="text-[#db8654]" /> Location Name
                    </label>
                    <input
                        type="text"
                        name="locationName"
                        value={formData.locationName}
                        onChange={handleChange}
                        placeholder="e.g. Central Park, NY"
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#db8654] outline-none transition"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Latitude</label>
                    <input
                        type="number"
                        step="any"
                        name="lat"
                        value={formData.lat}
                        onChange={handleChange}
                        placeholder="e.g. 40.785091"
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#db8654] outline-none transition"
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Longitude</label>
                    <input
                        type="number"
                        step="any"
                        name="long"
                        value={formData.long}
                        onChange={handleChange}
                        placeholder="e.g. -73.968285"
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#db8654] outline-none transition"
                    />
                </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Tag size={14} className="text-[#db8654]" /> Vibe Tags (Select all that apply)
            </label>
            <div className="flex flex-wrap gap-2">
                {VIBE_OPTIONS.map(vibe => (
                    <button
                        key={vibe}
                        type="button"
                        onClick={() => handleVibeToggle(vibe)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            formData.vibeTags.includes(vibe) 
                            ? 'bg-[#db8654] text-white shadow-sm scale-105' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        {vibe}
                    </button>
                ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <ImageIcon size={14} className="text-[#db8654]" /> Event Gallery
                </label>
                <button
                    type="button"
                    onClick={() => {
                        setActiveMediaTarget("gallery");
                        setShowGallery(true);
                    }}
                    className="text-xs bg-gray-800 text-white px-3 py-1.5 rounded-lg hover:bg-gray-900 transition"
                >
                    Add Media
                </button>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {formData.gallery.map((item, index) => (
                    <div key={index} className="relative group rounded-xl overflow-hidden aspect-square border border-gray-100 shadow-sm">
                        <img src={ImgURL(item.url)} alt={`Gallery ${index}`} className="w-full h-full object-cover" />
                        <button
                            type="button"
                            onClick={() => handleRemoveGalleryItem(index)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            <X size={12} />
                        </button>
                    </div>
                ))}
                {formData.gallery.length === 0 && (
                    <div className="col-span-full py-8 text-center text-gray-400 text-sm italic border-2 border-dashed border-gray-100 rounded-xl">
                        No gallery images added yet
                    </div>
                )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-md p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                <ImageIcon size={14} className="text-[#db8654]" /> Cover Image
              </label>
              
              {formData.coverImageUrl ? (
                <div className="relative group rounded-xl overflow-hidden border border-gray-100 shadow-sm aspect-video mb-3">
                    <img src={ImgURL(formData.coverImageUrl)} alt="Preview" className="w-full h-full object-cover" />
                    <button 
                        type="button"
                        onClick={() => setFormData({...formData, coverImageUrl: ""})}
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                        <X size={14} />
                    </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-200 rounded-xl aspect-video flex flex-col items-center justify-center text-gray-400 mb-3">
                    <ImageIcon size={32} strokeWidth={1} />
                    <span className="text-xs mt-2">No image selected</span>
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                    setActiveMediaTarget("cover");
                    setShowGallery(true);
                }}
                className="w-full py-2.5 bg-gray-800 text-white rounded-xl hover:bg-gray-900 transition font-medium text-sm"
              >
                Choose from Gallery
              </button>
            </div>

            <div className="pt-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Event Type</label>
              <select
                name="eventType"
                value={formData.eventType}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#db8654] outline-none transition bg-white"
              >
                <option value="In-Person">In-Person</option>
                <option value="Virtual">Virtual</option>
                <option value="Eventbrite">Eventbrite</option>
                <option value="Ticketmaster">Ticketmaster</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                <DollarSign size={14} className="text-[#db8654]" /> Price ($)
              </label>
              <input
                type="number"
                name="price"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={handleChange}
                disabled={formData.source === 'Ticketmaster' || formData.source === 'Eventbrite'}
                title={formData.source === 'Ticketmaster' || formData.source === 'Eventbrite' ? "Price is managed by the external source" : ""}
                className={`w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#db8654] outline-none transition ${formData.source === 'Ticketmaster' || formData.source === 'Eventbrite' ? 'bg-gray-100 cursor-not-allowed opacity-80' : ''}`}
              />
              {(formData.source === 'Ticketmaster' || formData.source === 'Eventbrite') && (
                  <p className="text-[10px] text-gray-400 mt-1 italic">Note: Price is synced from {formData.source} and cannot be edited manually.</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">External Ticket URL</label>
              <input
                type="url"
                name="externalTicketUrl"
                value={formData.externalTicketUrl}
                onChange={handleChange}
                placeholder="e.g. https://ticketmaster.com/..."
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#db8654] outline-none transition"
              />
            </div>

            {formData.eventType === 'Virtual' && (
              <div className="pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
                <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2 text-blue-600">
                  <Tag size={14} /> Online Meeting Link (Zoom/Meet)
                </label>
                <input
                  type="url"
                  name="meetingLink"
                  value={formData.meetingLink}
                  onChange={handleChange}
                  placeholder="e.g. https://zoom.us/j/123456789"
                  className="w-full px-4 py-2.5 rounded-xl border-blue-200 bg-blue-50 focus:border-blue-500 outline-none transition"
                />
                <p className="text-[10px] text-blue-500 mt-1 italic">Note: This link will only be visible to users who have joined the event.</p>
              </div>
            )}

            <div className="pt-2">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className="relative">
                    <input
                        type="checkbox"
                        checked={formData.featured === 1}
                        onChange={(e) => setFormData({...formData, featured: e.target.checked ? 1 : 0})}
                        className="sr-only"
                    />
                    <div className={`w-10 h-6 rounded-full transition-colors ${formData.featured === 1 ? 'bg-orange-500' : 'bg-gray-300'}`}></div>
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.featured === 1 ? 'translate-x-4' : ''}`}></div>
                </div>
                <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900 transition-colors">Featured Event (Top of list)</span>
              </label>
            </div>

            <div className="pt-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#db8654] outline-none transition bg-white"
              >
                <option value="Published">Published (Live)</option>
                <option value="Pending">Pending (Approval)</option>
                <option value="Draft">Draft (Hidden)</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-[#db8654] text-white py-4 rounded-2xl font-bold shadow-lg hover:shadow-xl hover:bg-[#c47548] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : (
              <>
                <Save size={20} />
                {isEdit ? "Update Event" : "Create Event"}
              </>
            )}
          </button>
        </div>
      </form>

      {/* MEDIA GALLERY MODAL */}
      {showGallery && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-5xl h-[90vh] rounded-3xl shadow-2xl relative flex flex-col overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800">Media Gallery</h3>
                <button
                    className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition"
                    onClick={() => setShowGallery(false)}
                >
                    <X size={24} />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto">
                <AdminMediaGallery
                    accept="image/*"
                    onSelect={handleMediaSelect}
                />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventEdit;
